import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import ContentUnavailable from '../../../components/common/ContentUnavailable';
import Loader from '../../../components/common/Loader';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';

import LiveClass from './LiveClass';
import RecordedVideo from './RecordedVideo';
import Assignments from './Assignments';
import Assessments from './Assessments';
import Cummunity from './Cummunity';
import ClassroomCourses from './ClassroomCourses';
import { getTraineeClassroomsDashboardData, getUserClassroomAssignmentById, getUserClassroomAssessmentById, getUserClassroomLiveClassById, getUserClassroomResourceById } from '../../../services/user/classroom.services';
import {
  getChannelUnreadApi,
  markChannelReadApi,
} from '../../../services/getstream/getstream.service';
import { decodeData } from '../../../utils/encodeAndEncode';

/** Community classroom tab — hidden until the feature is supported end-to-end */
const SHOW_CLASSROOM_COMMUNITY_TAB = true;

const VALID_DASHBOARD_TABS = ['assignments', 'assessment', 'live', 'recorded', 'community', 'courses'];

const normalizeTabFromUrl = (tab) => {
  if (!tab) return null;
  if (tab === 'assessments') return 'assessment';
  return VALID_DASHBOARD_TABS.includes(tab) ? tab : null;
};

function UserClassroom() {
  const { encodedClassroomID } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const classroom_id = decodeData(decodeURIComponent(encodedClassroomID));
  const urlActiveTab = normalizeTabFromUrl(searchParams.get('active-tab'));
  const safeUrlTab =
    urlActiveTab === 'community' && !SHOW_CLASSROOM_COMMUNITY_TAB ? null : urlActiveTab;
  const linkedAssignmentId = searchParams.get('assignment');
  const linkedAssessmentId = searchParams.get('assessment');
  const linkedLiveClassId = searchParams.get('liveClass');
  const linkedResourceId = searchParams.get('resource');
  const [activeTab, setActiveTab] = useState(safeUrlTab || 'assignments');
  const [tabRefreshKey, setTabRefreshKey] = useState(0);
  const [classroomData, setClassroomData] = useState(null);
  const [, setClassroomName] = useState(null);
  const [tabVisibility, setTabVisibility] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classroomStatus, setClassroomStatus] = useState('loading');
  const [linkedContentStatus, setLinkedContentStatus] = useState('idle');
  const [communityUnread, setCommunityUnread] = useState(0);

  const patchSearchParams = (mutator) => {
    const next = new URLSearchParams(searchParams);
    mutator(next);
    setSearchParams(next, { replace: true });
  };

  // Check if all tabs are disabled
  // Courses remain available even when every configurable classroom feature is hidden.
  const areAllTabsDisabled = false;

  useEffect(() => {
    if (urlActiveTab && urlActiveTab !== activeTab) {
      setActiveTab(urlActiveTab);
    }
  }, [urlActiveTab, activeTab]);

  useEffect(() => {
    if (!searchParams.get('active-tab')) {
      patchSearchParams((next) => {
        next.set('active-tab', activeTab);
      });
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    let cancelled = false;

    const fetchClassroomData = async () => {
      try {
        setLoading(true);
        setClassroomStatus('loading');
        const response = await getTraineeClassroomsDashboardData({ class_id: classroom_id });

        // Handle different response structures (backend uses successResponse: { success, statusCode, message, data })
        let classroomData = null;
        const body = response.data || response;
        if ((body.statusCode === 200 || body.status === 200) && body.data) {
          classroomData = body.data;
        } else if (body && !body.statusCode && !body.status && body.cls_name) {
          classroomData = body;
        } else if (response?.success && response?.data?.cls_name) {
          classroomData = response.data;
        } else if (response?.success && response?.data) {
          classroomData = response.data;
        }

        if (cancelled) {
          return;
        }

        if (classroomData) {
          setClassroomStatus('available');
          setClassroomData(classroomData);
          setClassroomName(classroomData.cls_name);

          // Set tab visibility
          if (classroomData.is_visibility) {
            setTabVisibility(classroomData.is_visibility);

            // Only set default tab if no URL parameter is present
            if (!urlActiveTab) {
              // Set the first visible tab as active (skip disabled Community)
              let firstVisibleTab = classroomData.is_visibility.find(tab => tab.is_visible === 'true');
              if (
                firstVisibleTab?.tab_name === 'community' &&
                !SHOW_CLASSROOM_COMMUNITY_TAB
              ) {
                firstVisibleTab = classroomData.is_visibility.find(
                  (tab) => tab.is_visible === 'true' && tab.tab_name !== 'community'
                );
              }
              if (firstVisibleTab) {
                const tabMapping = {
                  assignment: 'assignments',
                  assessment: 'assessment',
                  liveclass: 'live',
                  recorded: 'recorded',
                  community: 'community',
                };
                const mappedTabName = tabMapping[firstVisibleTab.tab_name] || 'assignments';
                setActiveTab(mappedTabName);
                patchSearchParams((next) => {
                  next.set('active-tab', mappedTabName);
                });
              }
            }
          }
        } else {
          setClassroomStatus('unavailable');
        }
      } catch (error) {
        console.error('Error fetching classroom data:', error);
        if (!cancelled) {
          setClassroomStatus('unavailable');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (!classroom_id) {
      setClassroomStatus('unavailable');
      setLoading(false);
      return undefined;
    }

    if (classroom_id) {
      fetchClassroomData();
      setTabRefreshKey((key) => key + 1);
    }

    return () => {
      cancelled = true;
    };
  }, [encodedClassroomID, location.key, classroom_id, urlActiveTab]);

  useEffect(() => {
    if (linkedAssignmentId) {
      if (activeTab !== 'assignments') {
        setActiveTab('assignments');
        patchSearchParams((next) => {
          next.set('active-tab', 'assignments');
        });
      }
    } else if (linkedAssessmentId) {
      if (activeTab !== 'assessment') {
        setActiveTab('assessment');
        patchSearchParams((next) => {
          next.set('active-tab', 'assessment');
        });
      }
    } else if (linkedLiveClassId) {
      if (activeTab !== 'live') {
        setActiveTab('live');
        patchSearchParams((next) => {
          next.set('active-tab', 'live');
        });
      }
    } else if (linkedResourceId) {
      if (activeTab !== 'recorded') {
        setActiveTab('recorded');
        patchSearchParams((next) => {
          next.set('active-tab', 'recorded');
        });
      }
    }
  }, [linkedAssignmentId, linkedAssessmentId, linkedLiveClassId, linkedResourceId]);

  useEffect(() => {
    if (classroomStatus !== 'available' || !classroom_id) {
      return undefined;
    }

    if (!linkedAssignmentId && !linkedAssessmentId && !linkedLiveClassId && !linkedResourceId) {
      setLinkedContentStatus('idle');
      return undefined;
    }

    let cancelled = false;

    const verifyLinkedContent = async () => {
      setLinkedContentStatus('loading');
      try {
        if (linkedAssignmentId) {
          const response = await getUserClassroomAssignmentById({
            class_id: classroom_id,
            assignment_id: linkedAssignmentId,
          });
          if (response?.success && response?.data?.assignment) {
            if (!cancelled) setLinkedContentStatus('available');
            return;
          }
        }

        if (linkedAssessmentId) {
          const response = await getUserClassroomAssessmentById({
            class_id: classroom_id,
            assessment_id: linkedAssessmentId,
          });
          if (response?.success && response?.data?.assessment) {
            if (!cancelled) setLinkedContentStatus('available');
            return;
          }
        }

        if (linkedLiveClassId) {
          const response = await getUserClassroomLiveClassById({
            class_id: classroom_id,
            live_class_id: linkedLiveClassId,
          });
          if (response?.success && response?.data?.liveClass) {
            if (!cancelled) setLinkedContentStatus('available');
            return;
          }
        }

        if (linkedResourceId) {
          const response = await getUserClassroomResourceById({
            class_id: classroom_id,
            resource_id: linkedResourceId,
          });
          if (response?.success && response?.data?.resource) {
            if (!cancelled) setLinkedContentStatus('available');
            return;
          }
        }

        if (!cancelled) setLinkedContentStatus('unavailable');
      } catch (error) {
        if (!cancelled) setLinkedContentStatus('unavailable');
      }
    };

    verifyLinkedContent();

    return () => {
      cancelled = true;
    };
  }, [classroomStatus, classroom_id, linkedAssignmentId, linkedAssessmentId, linkedLiveClassId, linkedResourceId]);

  const getLinkedUnavailableContent = () => {
    if (linkedAssignmentId) {
      return {
        title: 'This assignment is not available',
        icon: 'mdi:file-document-remove-outline',
      };
    }
    if (linkedAssessmentId) {
      return {
        title: 'This assessment is not available',
        icon: 'mdi:clipboard-remove-outline',
      };
    }
    if (linkedLiveClassId) {
      return {
        title: 'This live class is not available',
        icon: 'mdi:video-off-outline',
      };
    }
    if (linkedResourceId) {
      return {
        title: 'This resource is not available',
        icon: 'mdi:folder-remove-outline',
      };
    }
    return {
      title: 'This content is not available',
      icon: 'mdi:alert-circle-outline',
    };
  };

  const handleDismissLinkedContent = () => {
    patchSearchParams((next) => {
      next.delete('assignment');
      next.delete('assessment');
      next.delete('liveClass');
      next.delete('resource');
    });
    setLinkedContentStatus('idle');
  };

  const markCommunitySeen = async (channelId) => {
    if (!channelId) return;
    try {
      await markChannelReadApi({ channelId });
    } catch (e) {
      // ignore
    }
    try {
      const uid = (() => {
        const u = JSON.parse(localStorage.getItem('user') || 'null');
        return u?.id ? String(u.id) : 'anon';
      })();
      localStorage.setItem(`community_last_seen:${uid}:${channelId}`, new Date().toISOString());
    } catch (e) {
      // ignore
    }
    setCommunityUnread(0);
  };

  const computeUnread = async (channelId) => {
    if (!channelId) return;
    const resp = await getChannelUnreadApi({ channelId });
    const unread = Number(resp?.data?.unread_count ?? 0) || 0;
    setCommunityUnread(unread);
  };

  const UNREAD_POLL_INTERVAL_MS = 30000;

  useEffect(() => {
    if (!SHOW_CLASSROOM_COMMUNITY_TAB) return;
    if (activeTab === 'community') return;
    const channelId = classroomData?.stream_channel_id;
    if (!channelId) return;

    let timer = null;
    const tick = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        await computeUnread(channelId);
      } catch (e) {
        // ignore
      }
    };

    tick();
    timer = setInterval(tick, UNREAD_POLL_INTERVAL_MS);
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [classroomData?.stream_channel_id, activeTab]);

  useEffect(() => {
    if (!SHOW_CLASSROOM_COMMUNITY_TAB || activeTab !== 'community') return;
    const channelId = classroomData?.stream_channel_id;
    if (!channelId) return;
    markCommunitySeen(channelId);
  }, [activeTab, classroomData?.stream_channel_id]);

  useEffect(() => {
    if (!SHOW_CLASSROOM_COMMUNITY_TAB && activeTab === 'community') {
      setActiveTab('assignments');
    }
  }, [activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    patchSearchParams((next) => {
      next.set('active-tab', tab);
      next.delete('assignment');
      next.delete('assessment');
      next.delete('liveClass');
      next.delete('resource');
    });
    setLinkedContentStatus('idle');
  };

  // Helper function to check if a tab is visible
  const isTabVisible = (tabName) => {
    if (tabName === 'community' && !SHOW_CLASSROOM_COMMUNITY_TAB) {
      return false;
    }
    // If tabVisibility is empty (not loaded yet), show all tabs by default
    if (tabVisibility.length === 0) {
      return true;
    }

    const tab = tabVisibility.find(t => t.tab_name === tabName);
    return tab ? tab.is_visible === "true" : false;
  };

  if (loading || classroomStatus === 'loading') {
    return <Loader variant="section" caption="Loading classroom..." />;
  }

  if (classroomStatus === 'unavailable') {
    return (
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full">
          <ContentUnavailable
            title="This classroom is not available"
            description="This classroom may have been deleted or removed. You no longer have access to it."
            icon="mdi:school-off-outline"
            backLabel="Back to Classrooms"
            backTo="/user/AllClassroom"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
      <div className="tw-min-w-[0px] ui-column tw-w-full">
        <div className={"[&_h5]:tw-mb-0 [&_h5]:[font-weight:var(--font-weight-semibold)] [&_h5]:tw-text-[var(--text-primary)] [&_h5]:tw-flex [&_h5]:tw-items-center [&_h5]:tw-gap-[var(--spacing-sm)] [&_h6]:tw-mb-0 [&_h6]:[font-weight:var(--font-weight-semibold)] [&_h6]:tw-text-[var(--text-primary)] [&_h6]:tw-flex [&_h6]:tw-items-center [&_h6]:tw-gap-[var(--spacing-sm)] [&_h5_.iconify]:tw-text-[1.25rem] [&_h5_.iconify]:tw-text-[var(--primary-color)] [&_h6_.iconify]:tw-text-[1.25rem] [&_h6_.iconify]:tw-text-[var(--primary-color)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] max-[768px]:tw-p-[var(--spacing-sm)_var(--spacing-md)] tw-border-solid tw-p-[var(--spacing-md)_var(--spacing-lg)] tw-rounded-tl-[0.75rem] tw-rounded-tr-[0.75rem] [--tw-border-opacity:1] tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-5 tw-pr-5 ui-card-header tw-bg-white tw-border-b-0 tw-flex tw-justify-between tw-items-center tw-py-4"}>
          <h4 className="tw-mb-0">{classroomData?.cls_name || "Classroom"}</h4>
        </div>

        {areAllTabsDisabled ? (
          <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card"}>
            <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
              <NoData
                message="No Active Tabs Available"
                description="The classroom features are currently disabled by the administrator. Please check back later or contact your instructor for more information."
                icon="fluent:presence-blocked-24-regular"
              />
            </div>
          </div>
        ) : (
          <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body tw-p-0">
            <div className="tw-border-solid tw-border-b-[#e0e0e0] tw-bg-[#f8f9fa] tab-header tw-border-b">
              <ul className={"max-[767.98px]:[&[aria-label=\"breadcrumb\"]]:!tw-p-[0.75rem_1rem] max-[767.98px]:[&[aria-label=\"breadcrumb\"]_h4]:tw-text-[1.1rem] tw-flex tw-gap-1 [list-style:none] [&_.ui-nav-item]:tw-m-0 [&_.ui-nav-link:hover]:tw-text-[#3152e8] [&_.ui-nav-link:hover]:tw-border-0 [&_.ui-nav-link.active::after]:[content:''] [&_.ui-nav-link.active::after]:tw-absolute [&_.ui-nav-link.active::after]:tw-bottom-0 [&_.ui-nav-link.active::after]:tw-left-0 [&_.ui-nav-link.active::after]:tw-w-full [&_.ui-nav-link.active::after]:tw-h-[2px] [&_.ui-nav-link.active::after]:tw-bg-[#3152e8] max-[768px]:tw-p-[0_10px] max-[768px]:[&_.ui-nav-link]:tw-p-[12px_15px] max-[768px]:[&_.ui-nav-link]:tw-text-[14px] [&_.ui-nav-link.disabled]:!tw-cursor-not-allowed [&_.ui-nav-link.disabled]:![pointer-events:all] [&_.ui-nav-link.disabled]:tw-opacity-[0.6] [&_button.disabled]:!tw-cursor-not-allowed [&_button.disabled]:![pointer-events:all] [&_button.disabled]:tw-opacity-[0.6] [&_button[disabled]]:!tw-cursor-not-allowed [&_button[disabled]]:![pointer-events:all] [&_button[disabled]]:tw-opacity-[0.6] [&_.ui-nav-link.active]:tw-text-[#3152e8] [&_.ui-nav-link.active]:tw-border-0 [&_.ui-nav-link.active]:tw-rounded-br-[0px] [&_.ui-nav-link.active]:tw-rounded-bl-[0px] [&_.ui-nav-link.active]:tw-border-b-[2px] [&_.ui-nav-link.active]:[--tw-border-opacity:1] [&_.ui-nav-link.active]:tw-border-[rgb(64_84_214_/_var(--tw-border-opacity,_1))] [&_.ui-nav-link.active]:tw-bg-[transparent] tw-border-0 tw-p-[0_20px] tw-mb-5 tw-border-b-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] tw-overflow-x-auto tw-flex-nowrap [scrollbar-width:thin] [&_.ui-nav-link]:tw-border-0 [&_.ui-nav-link]:tw-text-[#6c757d] [&_.ui-nav-link]:tw-p-[15px_25px] [&_.ui-nav-link]:tw-font-medium [&_.ui-nav-link]:[transition:all_0.3s_ease] [&_.ui-nav-link]:tw-relative [&_.ui-nav-link]:tw-whitespace-nowrap ui-nav ui-nav-tabs"}>
                {/* Assignments Tab */}
                {isTabVisible('assignment') && (
                  <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                    <button
                      className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "assignments" ? "active" : ""}`}
                      onClick={() => handleTabChange('assignments')}
                    >
                      <Icon icon="mdi:file-document" className="tw-me-2" width="20" height="20" />
                      Assignments
                    </button>
                  </li>
                )}

                {/* Assessment Tab */}
                {isTabVisible('assessment') && (
                  <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                    <button
                      className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "assessment" ? "active" : ""}`}
                      onClick={() => handleTabChange('assessment')}
                    >
                      <Icon icon="mdi:clipboard-text" className="tw-me-2" width="20" height="20" />
                      Assessment
                    </button>
                  </li>
                )}

                {/* Live Classes Tab */}
                {isTabVisible('liveclass') && (
                  <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                    <button
                      className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "live" ? "active" : ""}`}
                      onClick={() => handleTabChange('live')}
                    >
                      <Icon icon="mdi:video" className="tw-me-2" width="20" height="20" />
                      Live Classes
                    </button>
                  </li>
                )}


                {/* Recorded Videos Tab */}
                {isTabVisible('recorded') && (
                  <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                    <button
                      className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "recorded" ? "active" : ""}`}
                      onClick={() => handleTabChange('recorded')}
                    >
                      <Icon icon="mdi:play-circle" className="tw-me-2" width="20" height="20" />
                      Resources & Videos
                    </button>
                  </li>
                )}

                {SHOW_CLASSROOM_COMMUNITY_TAB && isTabVisible('community') && (
                  <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                    <button
                      className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "community" ? "active" : ""}`}
                      onClick={() => handleTabChange('community')}
                    >
                      <Icon icon="mdi:chat" className="tw-me-2" width="20" height="20" />
                      Community
                      {communityUnread > 0 && activeTab !== 'community' && (
                        <span className={"[&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-2.5 tw-pt-1 tw-pb-1 tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] ui-badge tw-rounded-full tw-bg-rose-600 tw-ms-2"}>
                          {communityUnread}
                        </span>
                      )}
                    </button>
                  </li>
                )}

                <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] ui-nav-item"}>
                  <button
                    className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium tw-text-slate-500 ui-nav-link tw-flex tw-items-center ${activeTab === 'courses' ? 'active' : ''}`}
                    onClick={() => handleTabChange('courses')}
                  >
                    <Icon icon="fluent:book-open-24-regular" className="tw-me-2" width="20" height="20" />
                    Courses
                  </button>
                </li>

              </ul>
            </div>

            {linkedContentStatus === 'loading' ? (
              <div className="tw-p-6">
                <Loader variant="section" caption="Opening content from your notification..." />
              </div>
            ) : linkedContentStatus === 'unavailable' ? (
              <div className="tw-p-6">
                <ContentUnavailable
                  title={getLinkedUnavailableContent().title}
                  description="It may have been removed, cancelled, or deleted from this classroom."
                  icon={getLinkedUnavailableContent().icon}
                  backLabel="View Classroom"
                  onBack={handleDismissLinkedContent}
                />
              </div>
            ) : (
              <>
            {/* Tab Content */}
            {activeTab === 'assignments' && (
              <div className="assignments">
                <Assignments key={`assignments-${tabRefreshKey}`} classroom_id={classroom_id} />
              </div>
            )}

            {activeTab === 'assessment' && (
              <div className="assessment">
                <Assessments key={`assessment-${tabRefreshKey}`} classroom_id={classroom_id} />
              </div>
            )}

            {activeTab === 'live' && (
              <div className="live-class">
                <LiveClass key={`live-${tabRefreshKey}`} classroom_id={classroom_id} />
              </div>
            )}

            {activeTab === 'recorded' && (
              <div className="recorded">
                <RecordedVideo key={`recorded-${tabRefreshKey}`} classroom_id={classroom_id} />
              </div>
            )}

            {SHOW_CLASSROOM_COMMUNITY_TAB && activeTab === 'community' && (
              <div className="community">
                {loading ? (
                  <Loader variant="tab" caption="Loading community..." />
                ) : (
                  <Cummunity
                    key={`community-${tabRefreshKey}`}
                    classroom_id={classroom_id}
                    stream_channel_id={classroomData?.stream_channel_id}
                    group_id={classroomData?.group?.group_id}
                    group_name={classroomData?.group?.group_name}
                  />
                )}
              </div>
            )}

            {activeTab === 'courses' && (
              <ClassroomCourses key={`courses-${tabRefreshKey}`} classroomId={classroom_id} />
            )}


              </>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

export default UserClassroom;
